import dayjs from 'dayjs'
import { queryClient } from '@/lib/react-query'
import { getApiReady } from '@/hooks/useApiReady'
import { useAuthStore } from '@/store/useAuthStore'
import { menuPermissions, publicRoutes, USER_ROLES } from '@/config/permissions'
import { getFreelancersListQueryOptions } from '@/hooks/queries/useFreelancersList'
import { getMissionsListQueryOptions } from '@/hooks/queries/useMissionsList'
import { getActivityReportsQueryOptions } from '@/hooks/queries/useActivityReports'
import { getDevicesToolsQueryOptions } from '@/hooks/queries/useDevicesTools'
import { clientKeys } from '@/hooks/queries/useClientContacts'
import { clientService } from '@/services/clientService'
import { handbooksKeys } from '@/hooks/queries/useHandbooks'
import handbookService from '@/services/handbookService'
import { faqsKeys } from '@/hooks/queries/useFaqs'
import faqService from '@/services/faqService'
import { filesKeys } from '@/hooks/queries/useFiles'
import { fetchFilesContracts } from '@/apis/files'
import { complianceAuditKeys } from '@/hooks/queries/useComplianceAudit'
import { fetchComplianceAudit } from '@/apis/complianceAudit'
import { accountingEntriesKeys } from '@/hooks/queries/useAccountingEntries'
import accountingService from '@/services/accountingService'
import { dashboardDataKeys } from '@/hooks/queries/useDashboardData'
import { reportingService } from '@/services/reportingService'
import {
  getDefaultReportingPeriod,
  reportingPeriodKey,
} from '@/lib/reporting/reportingPeriod'
import availabilityService from '@/services/availabilityService'
import {
  getSuperAdminListQueryOptions,
  SUPER_ADMIN_DEFAULT_LIST_FILTERS,
} from '@/hooks/queries/useSuperAdminData'
import { resetLazyModuleCache } from '@/utils/lazyWithRetry'

import {
  TEAM_MISSIONS_DEFAULT_STATUS,
  TEAM_MISSIONS_DEFAULT_PAGE_SIZE,
  FREELANCERS_DEFAULT_STATUS,
} from '@/lib/missions/missionsListDefaults'

const PREFETCH_DELAY_MS = 100

const prefetchedKeys = new Set<string>()

/** Routes sidebar avec prefetch API (doit couvrir tous les item.paths visibles). */
const SIDEBAR_API_PREFETCH_PATHS = new Set([
  '/freelancers',
  '/clients',
  '/missions',
  '/activity-report',
  '/accounting',
  '/reporting',
  '/files',
  '/audit',
  '/assets',
  '/devices-tools',
  '/faq',
  '/availability',
  '/our-partners',
  '/super-admin',
  '/super-admin/business',
  '/super-admin/kyc',
  '/super-admin/templates',
  '/super-admin/blog',
])

export interface PrefetchSidebarRouteParams {
  path: string
  role: string | null
  activeTeamId: string | null | undefined
}

function prefetchCacheKey(path: string, teamId: string | null | undefined): string {
  return `${path}:${teamId ?? 'none'}`
}

function normalizePrefetchPath(path: string): string {
  const p = path.split('?')[0]
  if (p.startsWith('/super-admin/kyc')) return '/super-admin/kyc'
  if (p.startsWith('/super-admin/templates')) return '/super-admin/templates'
  if (p.startsWith('/super-admin/blog')) return '/super-admin/blog'
  if (p.startsWith('/super-admin/business')) return '/super-admin/business'
  if (p.startsWith('/super-admin')) return '/super-admin'
  if (p.startsWith('/missions')) return '/missions'
  const segments = p.split('/').filter(Boolean)
  return segments.length ? `/${segments[0]}` : p
}

/** Planifie un prefetch au survol (debounce 100 ms). Données API uniquement — pas de import() JS. */
export function scheduleSidebarRoutePrefetch(params: PrefetchSidebarRouteParams): () => void {
  const timer = setTimeout(() => {
    void prefetchSidebarRoute(params)
  }, PREFETCH_DELAY_MS)
  return () => clearTimeout(timer)
}

/**
 * Lance le prefetch API de la route courante dès l'hydratation session (reload F5).
 * Parallèle au téléchargement des chunks lazy — la page lit le cache React Query au mount.
 */
export function prefetchInitialRouteAfterSessionHydrate(params: {
  role: string
  activeTeamId: string | null
}): void {
  if (typeof window === 'undefined') return

  const pathname = window.location.pathname
  if (publicRoutes.some((p) => pathname.startsWith(p))) return

  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return

  void prefetchSidebarRoute(
    {
      path: `/${segments[0]}`,
      role: params.role,
      activeTeamId: params.activeTeamId,
    },
    { ignoreApiReady: true }
  )
}

export async function prefetchSidebarRoute(
  { path, role, activeTeamId }: PrefetchSidebarRouteParams,
  options?: PrefetchSidebarRouteOptions
): Promise<void> {
  const normalizedPath = normalizePrefetchPath(path.split('?')[0])
  const cacheKey = prefetchCacheKey(normalizedPath, activeTeamId ?? null)

  if (prefetchedKeys.has(cacheKey)) return
  if (useAuthStore.getState().sessionTransitioning || useAuthStore.getState().sessionSilentSwap) return
  if (!options?.ignoreApiReady && !getApiReady()) return

  prefetchedKeys.add(cacheKey)

  const isFreelancer = role === USER_ROLES.FREELANCER
  const isTeam = role === USER_ROLES.TEAM_ADMIN || role === USER_ROLES.TEAM_MEMBER
  const reportMonth = dayjs().format('YYYY-MM')

  try {
    switch (normalizedPath) {
      case '/freelancers':
        if (!isTeam) break
        await queryClient.prefetchQuery(
          getFreelancersListQueryOptions({
            status: [...FREELANCERS_DEFAULT_STATUS],
            sortBy: 'created_at',
            sortDirection: 'desc',
          })
        )
        break

      case '/missions':
        await queryClient.prefetchQuery(
          getMissionsListQueryOptions({
            page: 1,
            pageSize: TEAM_MISSIONS_DEFAULT_PAGE_SIZE,
            status: isTeam ? [...TEAM_MISSIONS_DEFAULT_STATUS] : [],
            sortBy: 'created_at',
            sortDirection: 'desc',
            teamId: activeTeamId || undefined,
          })
        )
        break

      case '/activity-report':
        await queryClient.prefetchQuery(
          getActivityReportsQueryOptions(
            {
              page: 1,
              pageSize: 10,
              status: isTeam ? ['pending', 'approved'] : [],
              sortBy: 'created_at',
              sortDirection: 'desc',
              teamId: activeTeamId || undefined,
            },
            { isFreelancer, effectiveTeamId: activeTeamId ?? null }
          )
        )
        break

      case '/clients':
        if (!isTeam) break
        await queryClient.prefetchQuery({
          queryKey: clientKeys.list({
            page: 1,
            pageSize: 10,
            sortKey: 'created_at',
            sortDirection: 'desc',
          }),
          queryFn: () =>
            clientService.getClients({
              page: 1,
              pageSize: 10,
              sortKey: 'created_at',
              sortDirection: 'desc',
            }),
        })
        break

      case '/devices-tools':
        if (isFreelancer && !activeTeamId) break
        await queryClient.prefetchQuery(
          getDevicesToolsQueryOptions({
            page: 1,
            limit: 10,
            asset_type: 'device',
            team_id: isFreelancer ? activeTeamId || undefined : undefined,
          })
        )
        break

      case '/assets':
        await queryClient.prefetchQuery({
          queryKey: handbooksKeys.list({
            page: 1,
            pageSize: 10,
            search: '',
            teamId: isFreelancer ? activeTeamId : undefined,
          }),
          queryFn: async () => {
            const response = await handbookService.getHandbooks({
              page: 1,
              pageSize: 10,
              search: '',
              team_id: isFreelancer ? activeTeamId || undefined : undefined,
            })
            if (response.success) {
              const payload = response.data || {}
              return {
                list: payload.list || [],
                total: payload.total ?? payload.list?.length ?? 0,
                pageSize: payload.pageSize ?? 10,
              }
            }
            return { list: [], total: 0, pageSize: 10 }
          },
        })
        break

      case '/faq':
        await queryClient.prefetchQuery({
          queryKey: faqsKeys.list({
            search: '',
            teamId: isFreelancer ? activeTeamId : undefined,
          }),
          queryFn: async () => {
            const response = await faqService.getFaqs({
              search: '',
              team_id: isFreelancer ? activeTeamId || undefined : undefined,
            })
            if (response.success) {
              return {
                list: response.data.list,
                total: response.data.total,
              }
            }
            return { list: [], total: 0 }
          },
        })
        break

      case '/files':
        if (!isTeam) break
        await queryClient.prefetchQuery({
          queryKey: filesKeys.contracts({
            page: 1,
            per_page: 10,
            sort_by: 'date',
            sort_direction: 'desc',
          }),
          queryFn: async () => {
            const res = await fetchFilesContracts({
              page: 1,
              per_page: 10,
              sort_by: 'date',
              sort_direction: 'desc',
            })
            const payload = (
              res as {
                data?: {
                  items?: unknown[]
                  total?: number
                  per_page?: number
                  current_page?: number
                }
              }
            )?.data
            return {
              items: payload?.items ?? [],
              total: payload?.total ?? 0,
              per_page: payload?.per_page ?? 10,
              current_page: payload?.current_page ?? 1,
            }
          },
        })
        break

      case '/audit':
        if (!isTeam || role !== USER_ROLES.TEAM_ADMIN || !activeTeamId) break
        await queryClient.prefetchQuery({
          queryKey: complianceAuditKeys.detail(activeTeamId, reportMonth),
          queryFn: async () => {
            const res = await fetchComplianceAudit(reportMonth, activeTeamId)
            if (res.code === 200 && res.data) return res.data
            throw new Error(res.message || 'Failed to fetch compliance audit')
          },
        })
        break

      case '/accounting':
        if (!isTeam || role !== USER_ROLES.TEAM_ADMIN || !activeTeamId) break
        await queryClient.prefetchQuery({
          queryKey: accountingEntriesKeys.list({
            page: 1,
            pageSize: 8,
            sortBy: 'createdAt',
            sortDirection: 'desc',
            team_id: activeTeamId,
          }),
          queryFn: async () => {
            const { success, data: entries, total: totalCount } = await accountingService.getEntries({
              page: 1,
              pageSize: 8,
              sortBy: 'createdAt',
              sortDirection: 'desc',
              team_id: activeTeamId,
            })
            return { entries: success ? entries : [], total: totalCount ?? 0 }
          },
        })
        break

      case '/reporting': {
        if (!isTeam || role !== USER_ROLES.TEAM_ADMIN || !activeTeamId) break
        const defaultReportingPeriod = getDefaultReportingPeriod()
        await queryClient.prefetchQuery({
          queryKey: dashboardDataKeys.detail(reportingPeriodKey(defaultReportingPeriod), activeTeamId),
          queryFn: async () => {
            const response = await reportingService.getDashboard(defaultReportingPeriod, activeTeamId)
            if (!response.success || !response.data) {
              throw new Error(response.error || 'Failed to fetch dashboard data')
            }
            return response.data
          },
        })
        break
      }

      case '/availability':
        if (!activeTeamId) break
        if (isFreelancer) {
          await queryClient.prefetchQuery({
            queryKey: ['availability', 'me', reportMonth, activeTeamId],
            queryFn: async () => {
              const result = await availabilityService.getMyAvailability(reportMonth, activeTeamId)
              if (result.success) return result.data
              throw new Error(result.error)
            },
          })
        } else {
          const fromStr = dayjs().startOf('month').format('YYYY-MM-DD')
          const toStr = dayjs().endOf('month').format('YYYY-MM-DD')
          await queryClient.prefetchQuery({
            queryKey: ['availability', 'team', fromStr, toStr, activeTeamId],
            queryFn: async () => {
              const result = await availabilityService.getAvailability(
                { from: fromStr, to: toStr },
                activeTeamId
              )
              if (result.success) return result.data || []
              throw new Error(result.error)
            },
          })
        }
        break

      case '/our-partners':
        break

      case '/super-admin':
        if (role !== USER_ROLES.SUPER_ADMIN) break
        await queryClient.prefetchQuery(
          getSuperAdminListQueryOptions('companies', SUPER_ADMIN_DEFAULT_LIST_FILTERS)
        )
        break

      case '/super-admin/business':
      case '/super-admin/kyc':
      case '/super-admin/templates':
      case '/super-admin/blog':
        // Pas de liste API lourde au mount — contenu statique ou onglets dédiés
        break

      default:
        break
    }
  } catch {
    prefetchedKeys.delete(cacheKey)
  }
}

/** Réinitialise le cache de prefetch (switch d'équipe). */
export function resetSidebarPrefetchCache(): void {
  prefetchedKeys.clear()
  resetLazyModuleCache()
}

if (import.meta.env.DEV) {
  for (const item of menuPermissions) {
    if (item.hidden) continue
    for (const path of item.paths) {
      const normalized = normalizePrefetchPath(path)
      if (!SIDEBAR_API_PREFETCH_PATHS.has(normalized)) {
        console.warn(`[prefetchSidebarRoute] Missing API prefetch for sidebar path: ${path}`)
      }
    }
  }
}
